import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { backendApiServiceUrl, createApiDefaults } from './base';
import * as yup from 'yup';
import { axiosBaseQueryWithAuth } from './axios';
import { AbortController } from 'node-abort-controller';

interface MarketShareSegment {
  desktop_searches: number;
  mobile_searches: number;
  tablet_searches: number;
  segment_id: string;
  segment_name: string;
  total_searches: number;
}

interface Domain {
  top_data: string;
  totalclicks: string;
}

interface MasterDataResponse {
  market_share_segment: MarketShareSegment[];
  domain: Domain[];
}

interface savedSegment {
  text: string;
  value: string;
  definition: string;
  additional_info: string;
  id: string | number; // Use 'number' if 'id' is a number, 'string' if it's a string
}

export const commonApi = createApi({
  ...createApiDefaults,
  refetchOnFocus: false,
  baseQuery: axiosBaseQueryWithAuth({ baseUrl: backendApiServiceUrl }),
  reducerPath: 'common', // this is a legacy name. if this is changed, everyone will be logged out
  tagTypes: ['common', 'trends', 'CR_LIST', 'CR_PAGE_CONTEXT', 'AIV_METADATA', 'USABILITY_PAGES', 'MASTER_DATA'],

  endpoints: (builder) => ({
    getMasterData: builder.query<MasterDataResponse, string[]>({
      query: (params) => {
        return {
          url: `master-data`,
          method: 'POST',
          data: {
            params,
          },
        };
      },
      // Own tag ON TOP of the legacy 'common' one (same pattern as getAiVisibilityMetadata):
      // a Market Share tracker mutation can then refresh every cached master-data entry,
      // whichever params array each page fetched with, without refetching everything
      // 'common'-tagged.
      providesTags: ['common', 'MASTER_DATA'],
    }),
    getTrendsData: builder.query({
      query: (params) => {
        const { signal, ...requestParams } = params || {};
        return {
          url: `explore/trends`,
          method: 'POST',
          data: {
            params: requestParams,
          },
        };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        const { signal, ...rest } = queryArgs || {};
        return rest;
      },
      providesTags: ['trends'],
    }),
    getAiVisibilityData: builder.query({
      query: (params) => {
        return {
          url: `explore/ai-visibility`,
          method: 'POST',
          data: {
            params,
          },
        };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        const { signal, ...rest } = queryArgs || {};
        return rest;
      },
      providesTags: ['trends'],
    }),
    getAiVisibilityBatch: builder.query({
      query: (params) => {
        return {
          url: `explore/ai-visibility-batch`,
          method: 'POST',
          data: params,
        };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        const { signal, ...rest } = queryArgs || {};
        return rest;
      },
      providesTags: ['trends'],
    }),
    getAiVisibilityMetadata: builder.query({
      query: () => {
        return {
          url: `ai-visibility/metadata`,
          method: 'GET',
        };
      },
      // Own tag ON TOP of the legacy 'common' one so tracker mutations can
      // invalidate just this payload (segment mapping / paused state) without
      // refetching every 'common'-tagged endpoint.
      providesTags: ['common', 'AIV_METADATA'],
    }),
    getAiVisibilityPromptCount: builder.query({
      query: (params) => {
        const qs = new URLSearchParams(params).toString();
        return {
          url: `ai-visibility/prompt-count?${qs}`,
          method: 'GET',
        };
      },
      providesTags: ['trends'],
    }),
    getAiVisibilityFilterOptionCounts: builder.query({
      query: (params) => {
        const qs = new URLSearchParams(params).toString();
        return {
          url: `ai-visibility/filter-option-counts?${qs}`,
          method: 'GET',
        };
      },
      providesTags: ['trends'],
    }),
    getAiVisibilityPromptResponses: builder.query({
      query: (params) => {
        const qs = new URLSearchParams(params).toString();
        return {
          url: `ai-visibility/prompt-responses?${qs}`,
          method: 'GET',
        };
      },
      providesTags: ['trends'],
    }),
    // C4 text chunks: reads persisted citation/text evidence first, with a
    // bounded raw-answer fallback for older scopes. `prompts` repeats as one
    // query param per prompt — prompts legitimately contain commas, so the
    // backend reads repetition, never a comma-join (URLSearchParams would
    // comma-join an array value).
    getAiVisibilityPageChunks: builder.query({
      query: (params) => {
        const { prompts, ...rest } = params || {};
        const qs = new URLSearchParams(rest);
        (Array.isArray(prompts) ? prompts : []).forEach((p) => qs.append('prompts', p));
        return {
          url: `ai-visibility/page-chunks?${qs.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['trends'],
    }),
    // C8 server export: the response body is the raw CSV text. The qwa-backend
    // proxy JSON-encodes it in transit (res.json(csvString)) and axios decodes
    // it back — a transparent round-trip for a text payload, so the caller
    // receives the exact CSV the nodejs endpoint wrote.
    getAiVisibilityCitationsExport: builder.query({
      query: (params) => {
        const qs = new URLSearchParams(params).toString();
        return {
          url: `ai-visibility/citations-export?${qs}`,
          method: 'GET',
        };
      },
      providesTags: ['trends'],
    }),
    getCRList: builder.query({
      query: (params) => {
        return {
          url: `recommendations/content-recommendations`,
          method: 'POST',
          data: {
            ...params,
          },
        };
      },
      providesTags: ['CR_LIST'],
    }),
    getUsabilityPages: builder.query({
      query: (params) => {
        return {
          url: `recommendations/usability-pages`,
          method: 'POST',
          data: {
            ...params,
          },
        };
      },
      providesTags: ['USABILITY_PAGES'],
    }),
    generatePageContext: builder.query({
      query: (params) => {
        return {
          url: `recommendations/content-recommendations/page-context`,
          method: 'POST',
          data: {
            ...params,
          },
        };
      },
      providesTags: ['CR_PAGE_CONTEXT'],
    }),
    getCustomerInfo: builder.query({
      query: () => {
        return {
          url: `customer-info`,
          method: 'GET',
        };
      },
      providesTags: ['common'],
    }),
    getSegmentDataWithType: builder.query<savedSegment[], string>({
      query: (type) => {
        return {
          url: `segment?type=${type}`,
          method: 'GET',
        };
      },
      transformResponse: (baseResponse: any) => {
        const data = baseResponse?.data?.map((obj) => {
          return {
            text: obj.name,
            value: obj.name,
            definition: obj.definition,
            additional_info: obj.additional_info,
            id: obj.id,
          };
        });
        return data;
      },
      providesTags: ['common'],
    }),
  }),
});

export async function dispatchMasterDataQuery(dispatch, params) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.getMasterData.initiate(params));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching master-data', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

export function dispatchTrendsDataQuery(dispatch, params, forceRefetch = false) {
  const { signal, ...requestParams } = params || {};
  let abortedByCaller = false;
  let actionResult = dispatch(commonApi.endpoints.getTrendsData.initiate(requestParams, { forceRefetch }));
  const abort = () => {
    abortedByCaller = true;
    actionResult.abort();
  };
  // initiate() joins a query already in flight for the same args, even with
  // forceRefetch. If an earlier caller aborted that query (cancel, then
  // immediately re-dispatch the same body), this caller receives its
  // AbortError without having aborted anything. Re-issue once in that case.
  const unwrapResult = (retried = false) =>
    actionResult.unwrap().catch((err) => {
      if (err?.name === 'AbortError' && !abortedByCaller && !retried) {
        actionResult = dispatch(commonApi.endpoints.getTrendsData.initiate(requestParams, { forceRefetch: true }));
        return unwrapResult(true);
      }
      throw err;
    });

  return {
    promise: unwrapResult()
      .then((res) => {
        return res ? JSON.parse(JSON.stringify(res)) : undefined;
      })
      .catch((err) => {
        console.error('dispatchTrendsDataQuery error:', err);
        throw err;
      }),
    abort,
  };
}

export function dispatchAiVisibilityQuery(dispatch, params, forceRefetch = false) {
  const abortController = new AbortController();
  const actionResult = dispatch(
    commonApi.endpoints.getAiVisibilityData.initiate(
      {
        ...params,
        signal: abortController.signal,
      },
      { forceRefetch }
    )
  );
  const abort = () => {
    abortController.abort();
    actionResult.abort();
  };

  return {
    promise: actionResult
      .unwrap()
      .then((res) => {
        return res ? JSON.parse(JSON.stringify(res)) : undefined;
      })
      .catch((err) => {
        console.error('dispatchAiVisibilityQuery error:', err);
        throw err;
      }),
    abort,
  };
}

export function dispatchAiVisibilityBatchQuery(dispatch, queries, forceRefetch = false) {
  const abortController = new AbortController();
  const actionResult = dispatch(
    commonApi.endpoints.getAiVisibilityBatch.initiate(
      {
        queries,
        signal: abortController.signal,
      },
      { forceRefetch }
    )
  );
  const abort = () => {
    abortController.abort();
    actionResult.abort();
  };

  return {
    promise: actionResult
      .unwrap()
      .then((res) => {
        return res ? JSON.parse(JSON.stringify(res)) : undefined;
      })
      .catch((err) => {
        console.error('dispatchAiVisibilityBatchQuery error:', err);
        throw err;
      }),
    abort,
  };
}

export function dispatchAiVisibilityMetadataQuery(dispatch, forceRefetch = false) {
  const actionResult = dispatch(commonApi.endpoints.getAiVisibilityMetadata.initiate(undefined, { forceRefetch }));

  return actionResult
    .unwrap()
    .then((res) => {
      return res ? JSON.parse(JSON.stringify(res)) : undefined;
    })
    .catch((err) => {
      console.error('dispatchAiVisibilityMetadataQuery error:', err);
      throw err;
    })
    .finally(() => {
      // Release the RTK subscription once settled — every consumer copies the
      // payload into local state. Each call previously leaked a permanent
      // subscription, pinning the cache entry alive for the whole session, so
      // tracker state changes (pause/resume/deploy) could never surface without
      // a full reload: keepUnusedDataFor never elapsed and non-forced initiates
      // kept replaying the stale entry.
      actionResult.unsubscribe();
    });
}

export function dispatchAiVisibilityPromptCountQuery(dispatch, params, forceRefetch = false) {
  const actionResult = dispatch(commonApi.endpoints.getAiVisibilityPromptCount.initiate(params, { forceRefetch }));

  return actionResult
    .unwrap()
    .then((res) => {
      return res ? JSON.parse(JSON.stringify(res)) : undefined;
    })
    .catch((err) => {
      console.error('dispatchAiVisibilityPromptCountQuery error:', err);
      throw err;
    });
}

export function dispatchAiVisibilityFilterOptionCountsQuery(dispatch, params, forceRefetch = false) {
  const actionResult = dispatch(
    commonApi.endpoints.getAiVisibilityFilterOptionCounts.initiate(params, { forceRefetch })
  );

  return actionResult
    .unwrap()
    .then((res) => {
      return res ? JSON.parse(JSON.stringify(res)) : undefined;
    })
    .catch((err) => {
      console.error('dispatchAiVisibilityFilterOptionCountsQuery error:', err);
      throw err;
    });
}

export function dispatchAiVisibilityPromptResponsesQuery(dispatch, params, forceRefetch = false) {
  const actionResult = dispatch(commonApi.endpoints.getAiVisibilityPromptResponses.initiate(params, { forceRefetch }));

  return actionResult
    .unwrap()
    .then((res) => {
      return res ? JSON.parse(JSON.stringify(res)) : undefined;
    })
    .catch((err) => {
      console.error('dispatchAiVisibilityPromptResponsesQuery error:', err);
      throw err;
    });
}

export function dispatchAiVisibilityPageChunksQuery(dispatch, params, forceRefetch = false) {
  const actionResult = dispatch(commonApi.endpoints.getAiVisibilityPageChunks.initiate(params, { forceRefetch }));

  return actionResult
    .unwrap()
    .then((res) => {
      return res ? JSON.parse(JSON.stringify(res)) : undefined;
    })
    .catch((err) => {
      console.error('dispatchAiVisibilityPageChunksQuery error:', err);
      throw err;
    });
}

/**
 * C8 citations export — resolves to the raw CSV text. Always force-refetched
 * (an export must reflect the data at click time) and unsubscribed right
 * after settling so the potentially-large CSV string doesn't linger in the
 * RTK cache.
 */
export function dispatchAiVisibilityCitationsExportQuery(dispatch, params): Promise<string | undefined> {
  const actionResult = dispatch(
    commonApi.endpoints.getAiVisibilityCitationsExport.initiate(params, { forceRefetch: true })
  );

  return actionResult
    .unwrap()
    .then((res) => (typeof res === 'string' ? res : undefined))
    .catch((err) => {
      console.error('dispatchAiVisibilityCitationsExportQuery error:', err);
      throw err;
    })
    .finally(() => {
      actionResult.unsubscribe();
    });
}

export async function dispatchCRListDataQuery(dispatch, params, forceRefetch = false) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.getCRList.initiate(params, { forceRefetch: forceRefetch }));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching trends-data', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

export async function dispatchUsabilityPagesQuery(dispatch, params, forceRefetch = false) {
  try {
    const actionResult = await dispatch(
      commonApi.endpoints.getUsabilityPages.initiate(params, { forceRefetch: forceRefetch })
    );
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching usability-pages', error);
    throw error;
  }
}

export async function dispatchCustomerInfoQuery(dispatch) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.getCustomerInfo.initiate(''));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching customer-info-data', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

export async function dispatchGeneratePagePageContext(dispatch, params) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.generatePageContext.initiate(params));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching GeneratePagePageContext', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

export async function dispatchGetSegmentDataWithType(dispatch, type) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.getSegmentDataWithType.initiate(type));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching segment-data', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

// Function to invalidate trends cache - call this when ranking factor keyword changes
export function invalidateTrendsCache(dispatch) {
  dispatch(commonApi.util.invalidateTags(['trends']));
}

/**
 * Invalidate the AI Visibility metadata cache — call after a tracker mutation
 * (pause / resume / archive / deploy) so the dashboard's segment mapping,
 * paused indicators and ordering fetch fresh on the next mount instead of
 * replaying the session-cached payload (the backend clears its own redis cache
 * on these mutations, so the next request returns the new state).
 */
export function invalidateAiVisibilityMetadataCache(dispatch) {
  dispatch(commonApi.util.invalidateTags(['AIV_METADATA']));
}

/**
 * Invalidate every cached master-data entry. Call it after a Market Share tracker mutation
 * (deploy / pause / resume / archive / rename) so the Competitive Trends segment dropdown
 * refetches and shows the new segment within the session, without a hard refresh. The backend
 * clears its own Redis master-data key on the same mutations, so the refetch returns the new
 * state rather than replaying the server-side cache.
 */
export function invalidateMasterDataCache(dispatch) {
  dispatch(commonApi.util.invalidateTags(['MASTER_DATA']));
}

export const {
  useGetMasterDataQuery,
  useGetCRListQuery,
  // useLazyGetMasterDataQuery,
  useGetSegmentDataWithTypeQuery,
  useGetCustomerInfoQuery,
  useGetTrendsDataQuery,
  useGetAiVisibilityDataQuery,
  // useLazyGetCustomerInfoQuery,
  useGeneratePageContextQuery,
  usePrefetch,
  middleware,
  reducer,
  endpoints,
  util,
  reducerPath,
} = commonApi;
export default commonApi;
